import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { createSlotChangeInvite } from '../../utils/slotChangeService';
import { friendlyServerError } from '../../utils/formValidation';
import { formatTime12Hour, getDayName } from '../../utils/schedulingUtils';

// "Send time-change form" (2026-09-16). Staff-only, from the student profile.
// One student, one parent, one email: createSlotChangeInvite writes the
// token-keyed invite, emails the parent a link to /change-time, and hands the
// link back so staff can also text it. Same Bootstrap modal shape as
// SendPortalInvite, which sits beside it on the profile.
//
// Prefill comes from the record (student.parent1), never typed from memory;
// staff can overtype. The server re-derives the CURRENT slot itself, so the
// modal never has to know it, and reports it back after the send.

const studentNameOf = (s) =>
	!s ? '' : s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim() || 'Student';

const describe = (slot) =>
	`${getDayName(slot.dayOfWeek)}s at ${formatTime12Hour(slot.startTime)} (${slot.location === 'HLDC' ? 'Burnsville' : slot.location})`;

const SendSlotChangeForm = ({ show, onHide, student, onSuccess }) => {
	const [recipientEmail, setRecipientEmail] = useState('');
	const [recipientName, setRecipientName] = useState('');
	const [customMessage, setCustomMessage] = useState('');
	const [sending, setSending] = useState(false);
	const [error, setError] = useState('');
	const [result, setResult] = useState(null);
	const [copied, setCopied] = useState(false);

	useEffect(() => {
		if (!show) return;
		const p1 = student?.parent1;
		setRecipientEmail((p1?.email || '').trim());
		setRecipientName(p1 ? `${p1.firstName || ''} ${p1.lastName || ''}`.trim() : '');
		setCustomMessage('');
		setError('');
		setResult(null);
		setCopied(false);
	}, [show, student]);

	const canSend = !!student?.id && recipientEmail.trim() !== '' && recipientName.trim() !== '' && !sending;

	const handleSend = async () => {
		// The button is also disabled, but a disabled attribute is never the
		// only guard on an action in this codebase.
		if (!canSend) return;
		setSending(true);
		setError('');
		try {
			const data = await createSlotChangeInvite({
				studentId: student.id,
				recipientEmail: recipientEmail.trim(),
				recipientName: recipientName.trim(),
				customMessage: customMessage.trim(),
			});
			setResult(data);
			if (onSuccess) onSuccess(data);
		} catch (err) {
			setError(friendlyServerError(err, "Couldn't send the form. Please try again."));
		} finally {
			setSending(false);
		}
	};

	const copyLink = async () => {
		if (!result?.formUrl) return;
		try {
			await navigator.clipboard.writeText(result.formUrl);
			setCopied(true);
		} catch (e) {
			setCopied(false);
		}
	};

	const name = studentNameOf(student);

	return (
		<Modal show={show} onHide={onHide} centered>
			<Modal.Header closeButton>
				<Modal.Title>Send time-change form{name ? ` for ${name}` : ''}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{result ? (
					<>
						<Alert variant='success' className='mb-3'>
							{result.redirected
								? 'Form created. The email was redirected to the test inbox (production email is off).'
								: `Sent to ${recipientEmail}.`}
						</Alert>
						<p className='mb-2'>
							<strong>Current time on file:</strong>{' '}
							{result.currentSlots?.length
								? result.currentSlots.map(describe).join(' and ')
								: 'none (no assigned weekly lesson)'}
						</p>
						<p className='mb-1'>
							<strong>Link</strong> (the same one in the email, good for 14 days, single use):
						</p>
						<Form.Control readOnly value={result.formUrl} onFocus={(e) => e.target.select()} />
						<div className='d-flex gap-2 mt-2'>
							<Button variant='outline-secondary' size='sm' onClick={copyLink}>
								{copied ? 'Copied' : 'Copy link'}
							</Button>
						</div>
						<p className='text-muted small mt-3 mb-0'>
							When the parent submits, the request lands on Scheduling, Registration as a
							time change. Assigning the new time releases the old one.
						</p>
					</>
				) : (
					<>
						<p className='text-muted small'>
							The parent gets an email with a link to the open weekly times. They pick every
							time that could work; nothing changes until you assign one.
						</p>
						{error && <Alert variant='danger'>{error}</Alert>}
						<Form.Group className='mb-3'>
							<Form.Label>Parent name</Form.Label>
							<Form.Control
								value={recipientName}
								onChange={(e) => setRecipientName(e.target.value)}
								placeholder='Parent name'
							/>
						</Form.Group>
						<Form.Group className='mb-3'>
							<Form.Label>Parent email</Form.Label>
							<Form.Control
								type='email'
								value={recipientEmail}
								onChange={(e) => setRecipientEmail(e.target.value)}
								placeholder='parent@example.com'
							/>
						</Form.Group>
						<Form.Group className='mb-1'>
							<Form.Label>
								Note to the parent <span className='text-muted'>(optional)</span>
							</Form.Label>
							<Form.Control
								as='textarea'
								rows={3}
								value={customMessage}
								onChange={(e) => setCustomMessage(e.target.value)}
								placeholder='You mentioned Sundays are getting hard, so here is what we have open.'
							/>
						</Form.Group>
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant='secondary' onClick={onHide}>
					{result ? 'Done' : 'Cancel'}
				</Button>
				{!result && (
					<Button variant='primary' onClick={handleSend} disabled={!canSend}>
						{sending ? 'Sending…' : 'Send form'}
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default SendSlotChangeForm;
